import { TranslationData } from '../services/translationService';

export interface GroupedTranslations {
  [prefix: string]: TranslationData[];
}

/**
 * Optimized grouping function with memoization
 * Groups translations by prefix and caches results for better performance
 */
export class TranslationGrouper {
  private cache = new Map<string, GroupedTranslations>();
  private cacheKey = '';

  /**
   * Groups translations by prefix with caching
   * @param translations Array of translation data
   * @param searchQuery Optional search query for filtering
   * @returns Grouped translations
   */
  groupTranslations(translations: TranslationData[], searchQuery: string = ''): GroupedTranslations {
    // Create cache key based on translations length and search query
    const newCacheKey = `${translations.length}-${searchQuery}`;
    
    // Return cached result if available
    if (this.cacheKey === newCacheKey && this.cache.has(newCacheKey)) {
      return this.cache.get(newCacheKey)!;
    }

    // Filter translations based on search query
    const filteredTranslations = searchQuery 
      ? this.filterTranslations(translations, searchQuery)
      : translations;

    // Group by prefix
    const groups = filteredTranslations.reduce((groups: GroupedTranslations, item) => {
      // Extract prefix (everything up to the first non-alphanumeric character)
      const prefix = item.key.key.match(/^[a-zA-Z0-9]+/)?.[0] || 'Other';
      
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(item);
      
      return groups;
    }, {});

    // Cache the result
    this.cache.set(newCacheKey, groups);
    this.cacheKey = newCacheKey;

    // Limit cache size to prevent memory leaks
    if (this.cache.size > 10) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    return groups;
  }

  /**
   * Filters translations based on search query
   * @param translations Array of translation data
   * @param searchQuery Search query string
   * @returns Filtered translations
   */
  private filterTranslations(translations: TranslationData[], searchQuery: string): TranslationData[] {
    const searchLower = searchQuery.toLowerCase();
    
    return translations.filter(data => {
      // Search in key
      if (data.key.key.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in translation strings
      return data.strings.some(str => 
        str.value.toLowerCase().includes(searchLower)
      );
    });
  }

  /**
   * Gets sorted prefixes from grouped translations
   * @param groupedTranslations Grouped translations object
   * @returns Sorted array of prefixes
   */
  getSortedPrefixes(groupedTranslations: GroupedTranslations): string[] {
    return Object.keys(groupedTranslations).sort((a, b) => a.localeCompare(b));
  }

  /**
   * Clears the cache
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheKey = '';
  }
}

// Export a singleton instance
export const translationGrouper = new TranslationGrouper();
